/**
 * Leviathan Web - Bill of Lading List Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * BOL list with overview stats and filtering
 */

import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  FileText,
  Plus,
  Search,
  Eye,
  Printer,
  Truck,
  Package,
  CheckCircle,
  Filter,
  RefreshCw,
  Calendar,
  User,
  MapPin,
} from 'lucide-react';
import { API_URL } from '@/config/api';

interface BOL {
  id: string;
  bolNumber: string;
  status: string;
  customerName: string;
  customerId: string;
  deliveryAddress: string;
  deliveryCity: string;
  deliveryState: string;
  deliveryZip: string;
  driverName: string | null;
  truckNumber: string | null;
  totalQuantity: number;
  totalAmount: number;
  deliveryDate: string | null;
  createdAt: string;
  customer?: {
    name: string;
  };
  driver?: {
    firstName: string;
    lastName: string;
  };
  truck?: {
    unitNumber: string;
  };
}

interface BOLStats {
  byStatus: Record<string, number>;
  totalDelivered: number;
  totalQuantity: number;
  totalAmount: number;
}

/**
 * Stat card component
 */
function StatCard({
  label,
  value,
  subValue,
  icon,
  color = 'steel',
}: {
  label: string;
  value: string | number;
  subValue?: string;
  icon: React.ReactNode;
  color?: 'steel' | 'amber' | 'success' | 'warning' | 'error';
}) {
  const borderColors = {
    steel: 'border-steel',
    amber: 'border-amber',
    success: 'border-success-green',
    warning: 'border-warning-amber',
    error: 'border-error-red',
  };

  const textColors = {
    steel: 'text-steel',
    amber: 'text-amber',
    success: 'text-success-green',
    warning: 'text-warning-amber',
    error: 'text-error-red',
  };

  return (
    <div className="relative stat-card">
      <div className={`absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 ${borderColors[color]}`} />
      <div className={`absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 ${borderColors[color]}`} />
      <div className={`absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 ${borderColors[color]}`} />
      <div className={`absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 ${borderColors[color]}`} />

      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex items-center space-x-2 mb-2">
          <div className={textColors[color]}>{icon}</div>
          <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
            {label}
          </span>
        </div>
        <div className={`text-2xl font-mono font-bold ${textColors[color]}`} style={{ textShadow: '0 0 10px currentColor' }}>
          {value}
        </div>
        {subValue && (
          <div className="text-xs text-text-tertiary font-mono mt-1">{subValue}</div>
        )}
      </div>
    </div>
  );
}

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: string }) {
  const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
    DRAFT: { color: 'text-text-secondary', bg: 'bg-panel-border/50', label: 'Draft' },
    PENDING: { color: 'text-warning-amber', bg: 'bg-warning-amber/10', label: 'Pending' },
    IN_TRANSIT: { color: 'text-blue-400', bg: 'bg-blue-400/10', label: 'In Transit' },
    DELIVERED: { color: 'text-amber', bg: 'bg-amber/10', label: 'Delivered' },
    SIGNED: { color: 'text-success-green', bg: 'bg-success-green/10', label: 'Signed' },
    COMPLETED: { color: 'text-success-green', bg: 'bg-success-green/10', label: 'Completed' },
    VOID: { color: 'text-error-red', bg: 'bg-error-red/10', label: 'Void' },
  };

  const defaultConfig = { color: 'text-text-secondary', bg: 'bg-panel-border/50', label: 'Draft' };
  const config = statusConfig[status] ?? defaultConfig;

  return (
    <span className={`px-2 py-1 text-[10px] uppercase tracking-wider font-mono ${config.color} ${config.bg}`}>
      {config.label}
    </span>
  );
}

/**
 * BOL List Page Component
 */
const BOLListPage = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [bols, setBols] = useState<BOL[]>([]);
  const [stats, setStats] = useState<BOLStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus, pagination.page]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', pagination.page.toString());
      params.append('limit', pagination.limit.toString());

      const [bolsRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/bol?${params}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
        fetch(`${API_URL}/api/bol/stats`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        }),
      ]);

      if (bolsRes.ok) {
        const data = await bolsRes.json();
        setBols(data.bols || []);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch BOL data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const formatQuantity = (qty: number) => {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(qty);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary font-mono uppercase tracking-wider">
            Bills of Lading
          </h1>
          <p className="text-sm text-text-secondary font-mono mt-1">
            Delivery documentation and tracking
          </p>
        </div>
        <Link
          to="/bol/new"
          className="flex items-center space-x-2 bg-amber text-background px-4 py-2 font-mono text-sm uppercase tracking-wider hover:bg-amber/80 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>New BOL</span>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard
          label="Draft"
          value={stats?.byStatus.draft || 0}
          icon={<FileText className="w-5 h-5" />}
          color="steel"
        />
        <StatCard
          label="In Transit"
          value={stats?.byStatus.in_transit || 0}
          icon={<Truck className="w-5 h-5" />}
          color="warning"
        />
        <StatCard
          label="Delivered"
          value={stats?.byStatus.delivered || 0}
          subValue="Awaiting signature"
          icon={<Package className="w-5 h-5" />}
          color="amber"
        />
        <StatCard
          label="Completed"
          value={stats?.byStatus.completed || 0}
          icon={<CheckCircle className="w-5 h-5" />}
          color="success"
        />
        <StatCard
          label="Total Delivered"
          value={formatQuantity(stats?.totalQuantity || 0)}
          subValue={formatCurrency(stats?.totalAmount || 0)}
          icon={<Package className="w-5 h-5" />}
          color="amber"
        />
      </div>

      {/* Search and Filters */}
      <div className="bg-panel-dark border border-panel-border p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Search */}
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search BOL number, customer..."
                className="w-full bg-background border border-panel-border text-text-primary pl-10 pr-4 py-2 font-mono text-sm focus:border-amber focus:outline-none"
              />
            </div>
          </form>

          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-text-tertiary" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-background border border-panel-border text-text-primary px-3 py-2 font-mono text-sm focus:border-amber focus:outline-none"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING">Pending</option>
              <option value="IN_TRANSIT">In Transit</option>
              <option value="DELIVERED">Delivered</option>
              <option value="SIGNED">Signed</option>
              <option value="COMPLETED">Completed</option>
              <option value="VOID">Void</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            onClick={fetchData}
            className="flex items-center space-x-2 px-4 py-2 border border-panel-border text-text-secondary hover:text-amber hover:border-amber transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* BOL Table */}
      <div className="bg-panel-dark border border-panel-border">
        {/* Geometric corners */}
        <div className="relative">
          <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
          <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
          <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
          <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-panel-border bg-background/50">
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  BOL #
                </th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Status
                </th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Customer
                </th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Delivery
                </th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Driver/Truck
                </th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Quantity
                </th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Amount
                </th>
                <th className="text-left text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Date
                </th>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] text-text-tertiary font-mono px-4 py-3">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-text-tertiary font-mono">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading BOLs...
                  </td>
                </tr>
              ) : bols.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-12 text-text-tertiary font-mono">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No bills of lading found
                  </td>
                </tr>
              ) : (
                bols.map((bol) => (
                  <tr
                    key={bol.id}
                    className="border-b border-panel-border/50 hover:bg-amber/5 transition-colors cursor-pointer"
                    onClick={() => navigate(`/bol/${bol.id}`)}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm text-amber">{bol.bolNumber}</span>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={bol.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-text-primary">{bol.customerName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1 text-text-secondary">
                        <MapPin className="w-3 h-3" />
                        <span className="font-mono text-xs">
                          {bol.deliveryCity}, {bol.deliveryState}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-sm text-text-primary">
                        {bol.driverName || bol.driver ? (
                          <span className="flex items-center space-x-1">
                            <User className="w-3 h-3 text-text-tertiary" />
                            <span>{bol.driverName || `${bol.driver?.firstName} ${bol.driver?.lastName}`}</span>
                          </span>
                        ) : (
                          '-'
                        )}
                      </div>
                      {(bol.truckNumber || bol.truck) && (
                        <div className="flex items-center space-x-1 text-text-tertiary text-xs">
                          <Truck className="w-3 h-3" />
                          <span>{bol.truckNumber || bol.truck?.unitNumber}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-sm text-text-primary">
                        {formatQuantity(bol.totalQuantity)} gal
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-sm text-amber">
                        {formatCurrency(bol.totalAmount)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center space-x-1 text-text-secondary">
                        <Calendar className="w-3 h-3" />
                        <span className="font-mono text-xs">
                          {formatDate(bol.deliveryDate || bol.createdAt)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end space-x-2" onClick={(e) => e.stopPropagation()}>
                        <Link
                          to={`/bol/${bol.id}`}
                          className="p-1.5 text-text-tertiary hover:text-amber transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        <Link
                          to={`/bol/${bol.id}/print`}
                          className="p-1.5 text-text-tertiary hover:text-amber transition-colors"
                          title="Print BOL"
                        >
                          <Printer className="w-4 h-4" />
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-panel-border">
            <div className="text-xs text-text-tertiary font-mono">
              Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
              >
                Prev
              </button>
              <span className="text-text-secondary font-mono text-sm">
                {pagination.page} / {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
                className="px-3 py-1 border border-panel-border text-text-secondary hover:text-amber hover:border-amber disabled:opacity-50 disabled:cursor-not-allowed font-mono text-sm"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BOLListPage;
